import axios from 'axios';
import type {
  GetTeamsResponse,
  GetSprintsResponse,
  GetSprintSummaryResponse,
  GetMetricTrendResponse,
  GetDashboardResponse,
  NlqRequest,
  NlqResponse,
  ApiResponse,
  MetricName,
  CreateTeamRequest,
  UpdateTeamRequest,
  CreateTeamResponse,
  UpdateTeamResponse,
  DeleteTeamResponse,
  InsightsResponse,
  ConfluenceReportResponse,
  SprintReportData,
  GetRoadmapResponse,
  GetRoadmapInsightsResponse,
  RoadmapResponse,
  RoadmapConfluencePreviewRequest,
  RoadmapConfluencePublishRequest,
  GetProductsResponse,
  CreateProductRequest,
  UpdateProductRequest,
  CreateProductResponse,
  UpdateProductResponse,
  DeleteProductResponse,
  UserPreferences,
  UpdatePreferencesRequest,
} from '@sprintpulse/shared';
import { getAccessToken, getIdToken, login as authLogin, refreshTokens } from './auth';
import { getConfig } from './config';

const client = axios.create({
  baseURL: '/api', // placeholder — overridden by interceptor
  headers: { 'Content-Type': 'application/json' },
});

// Set baseURL from runtime config (resolved after loadConfig)
client.interceptors.request.use((config) => {
  config.baseURL = getConfig().apiUrl;
  return config;
});

// Attach JWT token to all requests
// Use ID token (not access token) — it includes cognito:groups for admin checks
// and has aud=clientId which the API Gateway JWT authorizer validates
client.interceptors.request.use((config) => {
  const token = getIdToken() ?? getAccessToken();
  if (token && token !== 'local-dev-token') {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On 401: try token refresh, then retry the request. If refresh fails, redirect to login.
let isRefreshing = false;
let refreshQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Queue this request until the ongoing refresh completes
      return new Promise((resolve, reject) => {
        refreshQueue.push({
          resolve: (token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(client(originalRequest));
          },
          reject,
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshed = await refreshTokens();
    isRefreshing = false;

    if (refreshed) {
      const newToken = getIdToken() ?? getAccessToken();
      // Retry queued requests
      refreshQueue.forEach((q) => q.resolve(newToken!));
      refreshQueue = [];
      // Retry the original request
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return client(originalRequest);
    }

    // Refresh failed — reject queued requests and redirect to login
    refreshQueue.forEach((q) => q.reject(error));
    refreshQueue = [];
    authLogin();
    return Promise.reject(error);
  },
);

// ── Public API ────────────────────────────────────────────────────────────────
export const api = {
  getTeams: () =>
    client.get<GetTeamsResponse>('/teams').then((r) => r.data.data),

  getSprints: (teamId: string) =>
    client.get<GetSprintsResponse>(`/teams/${teamId}/sprints`).then((r) => r.data.data),

  getSprintSummary: (teamId: string, sprintId: string) =>
    client
      .get<GetSprintSummaryResponse>(`/teams/${teamId}/sprints/${sprintId}`)
      .then((r) => r.data.data),

  getMetricTrend: (teamId: string, metric: MetricName, sprints = 6) =>
    client
      .get<GetMetricTrendResponse>(`/teams/${teamId}/trends`, { params: { metric, sprints } })
      .then((r) => r.data.data),

  getDashboard: (productIds?: string[]) =>
    client.get<GetDashboardResponse>('/dashboard', {
      params: productIds && productIds.length > 0 ? { productIds: productIds.join(',') } : {},
    }).then((r) => r.data.data),

  getInsights: (teamId: string, sprintId: string) =>
    client.get<ApiResponse<InsightsResponse>>(
      `/teams/${teamId}/insights`, { params: { sprintId } }
    ).then((r) => r.data.data),

  generateConfluenceReport: (teamId: string, payload: { sprintId: string; parentPageUrl: string; pageTitle: string }) =>
    client.post<ApiResponse<ConfluenceReportResponse>>(
      `/teams/${teamId}/confluence-report`, payload
    ).then((r) => r.data.data),

  getConfluencePreview: (teamId: string, sprintId: string) =>
    client.get<ApiResponse<SprintReportData>>(
      `/teams/${teamId}/confluence-report/preview`, { params: { sprintId } }
    ).then((r) => r.data.data),

  publishConfluenceReport: (teamId: string, payload: {
    sprintId: string; parentPageUrl: string; pageTitle: string; sprintGoal: string; narrative: string;
  }) =>
    client.post<ApiResponse<ConfluenceReportResponse>>(
      `/teams/${teamId}/confluence-report`, payload
    ).then((r) => r.data.data),

  nlq: (req: NlqRequest) =>
    client.post<ApiResponse<NlqResponse>>('/nlq', req).then((r) => r.data.data),

  getProducts: () =>
    client.get<GetProductsResponse>('/products').then((r) => r.data.data),

  getRoadmap: (productId?: string) =>
    client.get<GetRoadmapResponse>('/roadmap', { params: productId ? { productId } : {} }).then((r) => r.data.data),

  getRoadmapDetails: (productId?: string) =>
    client.get<GetRoadmapResponse>('/roadmap/details', { params: productId ? { productId } : {} }).then((r) => r.data.data),

  getRoadmapInsights: (roadmapData: RoadmapResponse) =>
    client.post<GetRoadmapInsightsResponse>('/roadmap/insights', roadmapData).then((r) => r.data.data),

  getRoadmapConfluencePreview: (data: RoadmapConfluencePreviewRequest) =>
    client.post<ApiResponse<{ narrative: string }>>('/roadmap/confluence-report/preview', data).then((r) => r.data.data),

  publishRoadmapConfluenceReport: (data: RoadmapConfluencePublishRequest) =>
    client.post<ApiResponse<ConfluenceReportResponse>>('/roadmap/confluence-report', data).then((r) => r.data.data),
  syncAndCollect: (teamId?: string) =>
    client.post<ApiResponse<{ message: string }>>('/admin/sync', teamId ? { teamId } : {}).then((r) => r.data),

  // User preferences
  getPreferences: () =>
    client.get<ApiResponse<UserPreferences>>('/me/preferences').then((r) => r.data.data),

  updatePreferences: (req: UpdatePreferencesRequest) =>
    client.put<ApiResponse<UserPreferences>>('/me/preferences', req).then((r) => r.data.data),
};

// ── Admin API ─────────────────────────────────────────────────────────────────
export const adminApi = {
  listTeams: () =>
    client.get<GetTeamsResponse>('/admin/teams').then((r) => r.data.data),

  createTeam: (req: CreateTeamRequest) =>
    client.post<CreateTeamResponse>('/admin/teams', req).then((r) => r.data.data),

  updateTeam: (teamId: string, req: UpdateTeamRequest) =>
    client.patch<UpdateTeamResponse>(`/admin/teams/${teamId}`, req).then((r) => r.data.data),

  toggleTeam: (teamId: string) =>
    client.patch<UpdateTeamResponse>(`/admin/teams/${teamId}/toggle`).then((r) => r.data.data),

  deleteTeam: (teamId: string) =>
    client.delete<DeleteTeamResponse>(`/admin/teams/${teamId}`).then((r) => r.data.data),

  listProducts: () =>
    client.get<GetProductsResponse>('/admin/products').then((r) => r.data.data),

  createProduct: (req: CreateProductRequest) =>
    client.post<CreateProductResponse>('/admin/products', req).then((r) => r.data.data),

  updateProduct: (productId: string, req: UpdateProductRequest) =>
    client.patch<UpdateProductResponse>(`/admin/products/${productId}`, req).then((r) => r.data.data),

  deleteProduct: (productId: string) =>
    client.delete<DeleteProductResponse>(`/admin/products/${productId}`).then((r) => r.data.data),
};